import Phaser from 'phaser';

class StatsManager {
    constructor() {
        // Default stats structure
        this.stats = {
            health: 100,
            max_health: 100,
            energy: 100,
            max_energy: 100,
            hunger: 7,
            max_hunger: 7,
            money: 100 
        };
        
        this.walletAddress = null;
        this.userId = null;
        this.cachedUserId = null; // Cache user ID to avoid repeated lookups
        this.timers = {
            hungerDrain: null,
            energyUpdate: null,
            syncStats: null
        };
        
        this.events = new Phaser.Events.EventEmitter();
        
        // OPTIMIZED: Much longer sync intervals and batching
        this.SYNC_INTERVAL = 5 * 60 * 1000; // Sync every 5 minutes instead of 30 seconds
        this.ENERGY_UPDATE_INTERVAL = 60 * 1000 ; // Update energy every 10 seconds instead of every second
        this.HUNGER_DRAIN_INTERVAL = 5 * 60 * 1000; // 5 minutes (Note: first drain is delayed by 2.5 minutes on startup)
        this.DEBOUNCE_DELAY = 2000; // 2 second debounce for database writes
        
        this.isInitialized = false;
        this.pendingSync = false;
        this.syncDebounceTimer = null;
        this.lastSyncTime = 0;
        this.hasUnsyncedChanges = false;
        
        // Local state tracking to minimize database calls
        this.localChanges = {
            energy: false,
            hunger: false,
            health: false
        };
    }

    getGameService() {
        return window.gameService;
    }

    async initialize(userId, walletAddress) {
        this.userId = userId;
        this.cachedUserId = userId; // Cache the user ID immediately
        this.walletAddress = walletAddress;
        
        try {
            const gameService = this.getGameService();
            if (!gameService) {
                throw new Error('GameService not initialized');
            }

            gameService.setWalletAddress(this.walletAddress);
            
            // Get current stats from database
            const dbStats = await gameService.getStats();
            if (dbStats) {
                this.stats = { ...dbStats };
                this.isInitialized = true;
                
                // Start OPTIMIZED timers
                this.startOptimizedTimers();
                
                // Start MUCH less frequent periodic sync
                this.startPeriodicSync();
                
                // Emit initial update
                this.events.emit('stats-updated', this.stats);
                
                //console.log('StatsManager initialized with OPTIMIZED timers:', this.stats);
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Error initializing StatsManager:', error);
            return false;
        }
    }

    startOptimizedTimers() {
        // OPTIMIZED: Delayed hunger drain - first after 2.5 minutes, then every 5 minutes
        // This prevents immediate hunger loss on browser refresh/startup
        const firstHungerDrainDelay = 2.5 * 60 * 1000; // 2.5 minutes for first drain
        
        this.timers.hungerDrain = setTimeout(() => {
            // Process the first hunger drain
            this.processHungerDrain();
            
            // Then set up the regular interval for subsequent drains
            this.timers.hungerDrain = setInterval(() => {
                this.processHungerDrain();
            }, this.HUNGER_DRAIN_INTERVAL); // 5 minutes for subsequent drains
        }, firstHungerDrainDelay);

        // OPTIMIZED: Energy update timer (check every 10 seconds instead of every second)
        this.timers.energyUpdate = setInterval(() => {
            this.processEnergyUpdate();
        }, this.ENERGY_UPDATE_INTERVAL);
    }

    startPeriodicSync() {
        // OPTIMIZED: Much less frequent periodic sync (5 minutes vs 30 seconds)
        this.timers.syncStats = setInterval(() => {
            if (this.hasUnsyncedChanges) {
                this.debouncedSync();
            }
        }, this.SYNC_INTERVAL);
    }

    async processHungerDrain() {
        if (!this.isInitialized) return;

        if (this.stats.hunger > 0) {
            this.stats.hunger = Math.max(0, this.stats.hunger - 1);
            this.stats.last_hunger_drain = new Date().toISOString();
            
            this.events.emit('stats-updated', this.stats);
            
            // OPTIMIZED: Mark change but use debounced sync instead of immediate
            this.localChanges.hunger = true;
            this.hasUnsyncedChanges = true;
            this.debouncedSync();
        }
    }

    async processEnergyUpdate() {
        if (!this.isInitialized) return;

        const now = Date.now();
        const lastUpdate = this.stats.last_energy_update ? new Date(this.stats.last_energy_update).getTime() : now;
        const timeDiff = now - lastUpdate;

        // OPTIMIZED: Only process if enough time has passed (10 seconds instead of 1)
        if (timeDiff < this.ENERGY_UPDATE_INTERVAL) return;

        let energyChange = 0;
        
        if (this.stats.hunger === this.stats.max_hunger) {
            // Full hunger: gain energy faster (10 per update cycle)
            energyChange = 10;
        } else if (this.stats.hunger > 0) {
            // Partial hunger: gain energy slower (5 per update cycle)
            energyChange = 5;
        } else {
            // No hunger: lose energy (10 per update cycle, minimum 10)
            energyChange = -10;
        }

        if (energyChange !== 0) {
            const oldEnergy = this.stats.energy;
            
            if (energyChange > 0) {
                // Gaining energy
                this.stats.energy = Math.min(this.stats.max_energy, this.stats.energy + energyChange);
            } else {
                // Losing energy (but never below 10)
                this.stats.energy = Math.max(10, this.stats.energy + energyChange);
            }

            if (this.stats.energy !== oldEnergy) {
                this.stats.last_energy_update = new Date().toISOString();
                //console.log('Energy updated to:', this.stats.energy, '(change:', energyChange, ')');
                this.events.emit('stats-updated', this.stats);
                
                // OPTIMIZED: Mark change but use debounced sync
                this.localChanges.energy = true;
                this.hasUnsyncedChanges = true;
                this.debouncedSync();
            }
        }
    }

    // OPTIMIZED: Debounced sync to prevent excessive database calls
    debouncedSync() {
        // Clear existing timer
        if (this.syncDebounceTimer) {
            clearTimeout(this.syncDebounceTimer);
        }
        
        // Set new timer
        this.syncDebounceTimer = setTimeout(() => {
            this.syncToDatabase();
        }, this.DEBOUNCE_DELAY);
    }

    async syncToDatabase() {
        if (!this.isInitialized || this.pendingSync) return;
        
        // OPTIMIZED: Don't sync if no changes or recently synced
        const timeSinceLastSync = Date.now() - this.lastSyncTime;
        if (!this.hasUnsyncedChanges || timeSinceLastSync < this.DEBOUNCE_DELAY) {
            return;
        }

        this.pendingSync = true;
        
        try {
            const gameService = this.getGameService();
            if (!gameService) return;

            gameService.setWalletAddress(this.walletAddress);
            
            // OPTIMIZED: Only send changed stats to reduce payload
            const changedStats = {};
            if (this.localChanges.health) changedStats.health = this.stats.health;
            if (this.localChanges.energy) changedStats.energy = this.stats.energy;
            if (this.localChanges.hunger) changedStats.hunger = this.stats.hunger;
            
            if (Object.keys(changedStats).length === 0) {
                this.pendingSync = false;
                return;
            }
            
            //console.log('Syncing stat changes to database:', changedStats);
            
            const updatedStats = await gameService.updateStats(changedStats);

            if (updatedStats) {
                // Update our local stats with server response
                this.stats = { ...updatedStats };
                
                // Reset change tracking
                this.localChanges = { energy: false, hunger: false, health: false };
                this.hasUnsyncedChanges = false;
                this.lastSyncTime = Date.now();
                
                //console.log('Stats synced to database successfully');
            }
        } catch (error) {
            console.error('Error syncing stats to database:', error);
        } finally {
            this.pendingSync = false;
        }
    }

    // OPTIMIZED: Batch updates and use debounced sync
    async updateStats(statUpdates) {
        if (!this.isInitialized) {
            console.warn('StatsManager not initialized, cannot update stats');
            return false;
        }

        try {
            let hasChanges = false;
            
            // Update local stats with the provided values
            if (statUpdates.hunger !== undefined) {
                const newHunger = Math.max(0, Math.min(this.stats.max_hunger, statUpdates.hunger));
                if (newHunger !== this.stats.hunger) {
                    this.stats.hunger = newHunger;
                    this.localChanges.hunger = true;
                    hasChanges = true;
                }
            }
            if (statUpdates.energy !== undefined) {
                const newEnergy = Math.max(10, Math.min(this.stats.max_energy, statUpdates.energy));
                if (newEnergy !== this.stats.energy) {
                    this.stats.energy = newEnergy;
                    this.localChanges.energy = true;
                    hasChanges = true;
                }
            }
            if (statUpdates.health !== undefined) {
                const newHealth = Math.max(0, Math.min(this.stats.max_health, statUpdates.health));
                if (newHealth !== this.stats.health) {
                    this.stats.health = newHealth;
                    this.localChanges.health = true;
                    hasChanges = true;
                }
            }

            if (hasChanges) {
                // Emit stats-updated event immediately for UI updates
                this.events.emit('stats-updated', this.stats);

                // OPTIMIZED: Mark changes and use debounced sync
                this.hasUnsyncedChanges = true;
                this.debouncedSync();
            }

            return true;
        } catch (error) {
            console.error('Error updating stats:', error);
            return false;
        }
    }

    // OPTIMIZED: Force immediate sync when needed (for critical actions)
    async forceSyncToDatabase() {
        if (this.syncDebounceTimer) {
            clearTimeout(this.syncDebounceTimer);
            this.syncDebounceTimer = null;
        }
        await this.syncToDatabase();
    }

    async consumeItem(itemCode, categoryCode = 0, healAmount = 0, rarity = 'Normal', itemValue = 0, itemType = '') {

        if (!this.isInitialized) {
            throw new Error('StatsManager not initialized');
        }

        try {
            const gameService = this.getGameService();
            if (!gameService) {
                throw new Error('GameService not initialized');
            }

            gameService.setWalletAddress(this.walletAddress);

            const result = await gameService.consumeItem(itemCode, categoryCode, healAmount, rarity, itemValue, itemType);
            
            if (result && result.stats) {
                const oldStats = { ...this.stats };
                this.stats = { ...result.stats };
                this.events.emit('stats-updated', this.stats);
                this.events.emit('item-consumed', result.consumedItem);
                
                return result;
            } else {
                console.log('🍽️ STATSMANAGER: No result or stats from backend:', result);
            }
            
            return null;
        } catch (error) {
            console.error('Error consuming item:', error);
            throw error;
        }
    }

    // Heal player (used for consumables with healAmount)
    async healPlayer(amount) {
        if (!this.isInitialized) return false;

        const oldHealth = this.stats.health;
        this.stats.health = Math.min(this.stats.max_health, this.stats.health + amount);
        
        if (this.stats.health !== oldHealth) {
            this.events.emit('stats-updated', this.stats);
            await this.syncToDatabase();
            return true;
        }
        
        return false;
    }

    // Get stat percentages for UI
    getStatPercentages() {
        return {
            health: (this.stats.health / this.stats.max_health) * 100,
            energy: (this.stats.energy / this.stats.max_energy) * 100,
            hunger: (this.stats.hunger / this.stats.max_hunger) * 100
        };
    }

    // Get appropriate sprite frame based on stat percentage
    getStatFrame(statType) {
        const percentages = this.getStatPercentages();
        const percentage = percentages[statType];
        
        // Most sprites have 8 frames (0-7)
        // Frame 0 = full stat, Frame 7 = empty stat
        if (statType === 'hunger') {
            // Special hunger logic: full hunger shows frame 0, empty hunger alternates 6-7
            if (this.stats.hunger === 0) {
                // Alternate between frames 6 and 7 for empty hunger
                return Math.floor(Date.now() / 1000) % 2 === 0 ? 6 : 7;
            } else {
                // Normal hunger frames
                const frame = Math.floor((1 - percentage / 100) * 7);
                return Math.min(7, Math.max(0, frame));
            }
        } else {
            // Normal stat logic for health and energy
            const frame = Math.floor((1 - percentage / 100) * 7);
            return Math.min(7, Math.max(0, frame));
        }
    }

    getStats() {
        return { ...this.stats };
    }

    // Update player money
    async updateMoney(amount, operation = 'add') {
        if (!this.isInitialized) {
            throw new Error('StatsManager not initialized');
        }

        try {
            const gameService = this.getGameService();
            if (!gameService) {
                throw new Error('GameService not initialized');
            }

            gameService.setWalletAddress(this.walletAddress);
            
            const result = await gameService.updateMoney(amount, operation);
            
            if (result) {
                // Update local stats with new money value
                this.stats.money = result.money;
                this.events.emit('stats-updated', this.stats);
                return result;
            }
            
            return null;
        } catch (error) {
            console.error('Error updating money:', error);
            throw error;
        }
    }

    destroy() {
        // Clear all timers - handle both setTimeout and setInterval
        Object.values(this.timers).forEach(timer => {
            if (timer) {
                clearInterval(timer); // Clears setInterval
                clearTimeout(timer);  // Clears setTimeout (safe to call both)
            }
        });
        
        this.timers = {
            hungerDrain: null,
            energyUpdate: null,
            syncStats: null
        };
        
        this.events.removeAllListeners();
        this.isInitialized = false;
    }
}

// Export singleton instance
const statsManagerInstance = new StatsManager();

export const initStatsManager = (userId, walletAddress) => {
    return statsManagerInstance.initialize(userId, walletAddress);
};

export const getStatsManager = () => {
    return statsManagerInstance;
};

export { statsManagerInstance as statsManager }; 